import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { eventsApi } from '../../api/events.js';
import { engagementsApi } from '../../api/engagements.js';
import { speakersApi } from '../../api/speakers.js';
import { FormField, Input, Select, Textarea } from '../../components/common/FormField.jsx';
import { StatusBadge } from '../../components/common/StatusBadge.jsx';
import { ErrorAlert } from '../../components/common/ErrorAlert.jsx';
import { LoadingSpinner } from '../../components/common/LoadingSpinner.jsx';
import { Modal } from '../../components/common/Modal.jsx';
import { formatDate } from '../../utils/dates.js';
import { EVENT_STATUSES, EVENT_FORMATS, TIMEZONES, EVENT_COMM_TYPES } from '../../utils/constants.js';
import { buildEventMailtoLink } from '../../utils/mailto.js';
import { useSender } from '../../hooks/useSender.js';
import { generateSocialPost, X_CHAR_LIMIT } from '../../utils/socialTemplates.js';
import { usePageHeader } from '../../hooks/usePageHeader.jsx';

export function EventFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const sender = useSender();
  usePageHeader(isEdit ? 'Edit event' : 'New event');

  const [form, setForm] = useState({
    name: '', date: '', startTime: '', endTime: '',
    timeZone: 'America/New_York', format: 'In person',
    location: '', pduInfo: '', pduCode: '', notes: '', status: 'Draft',
  });
  const [engagements, setEngagements] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [emailModal, setEmailModal] = useState(false);
  const [emailType, setEmailType] = useState(EVENT_COMM_TYPES[0]);
  const [socialModal, setSocialModal] = useState(false);
  const [platform, setPlatform] = useState('linkedin');
  const [copied, setCopied] = useState(false);
  const [addSpeakerModal, setAddSpeakerModal] = useState(false);
  const [addSpeakerId, setAddSpeakerId] = useState('');
  const [addTalkTitle, setAddTalkTitle] = useState('');
  const [addTalkAbstract, setAddTalkAbstract] = useState('');
  const [addingSpeaker, setAddingSpeaker] = useState(false);

  const loadEngagements = () => {
    if (!isEdit) return;
    engagementsApi.list({ event: id }).then((eng) => setEngagements(eng ?? []));
  };

  useEffect(() => {
    if (!isEdit) return;
    Promise.all([eventsApi.get(id), engagementsApi.list({ event: id }), speakersApi.list()])
      .then(([ev, eng, spks]) => {
        setSpeakers(spks ?? []);
        setForm({
          name: ev.name ?? '',
          date: ev.date ?? '',
          startTime: ev.startTime ?? '',
          endTime: ev.endTime ?? '',
          timeZone: ev.timeZone ?? 'America/New_York',
          format: ev.format ?? 'In person',
          location: ev.location ?? '',
          pduInfo: ev.pduInfo ?? '',
          pduCode: ev.pduCode ?? '',
          notes: ev.notes ?? '',
          status: ev.status ?? 'Draft',
        });
        setEngagements(eng ?? []);
      })
      .catch((e) => setErrors({ _: e.message }))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const addSpeaker = async () => {
    if (!addSpeakerId) return;
    setAddingSpeaker(true);
    try {
      await engagementsApi.create({
        eventId: id,
        speakerId: addSpeakerId,
        talkTitle: addTalkTitle || undefined,
        talkAbstract: addTalkAbstract || undefined,
      });
      setAddSpeakerModal(false);
      setAddSpeakerId('');
      setAddTalkTitle('');
      setAddTalkAbstract('');
      loadEngagements();
    } catch (e) {
      setErrors({ _: e.message });
    } finally {
      setAddingSpeaker(false);
    }
  };

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!form.name.trim() || !form.date) {
      setErrors({ name: !form.name.trim() ? 'Event name is required' : undefined, date: !form.date ? 'Date is required' : undefined });
      return;
    }
    setSaving(true);
    try {
      if (isEdit) await eventsApi.update(id, form);
      else await eventsApi.create(form);
      navigate('/events');
    } catch (err) {
      setErrors({ _: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  // Speakers currently engaged for this event (excludes declined/cancelled) —
  // the recipient list for "Logistics reminder" and "Cancelled/rescheduled
  // notice". "Call for speakers" always sends with no recipients — there's
  // no engaged speaker to address by definition — so the organizer fills
  // in a shortlist themselves in their mail client.
  const engagedSpeakers = engagements
    .filter((e) => !['Declined', 'Cancelled'].includes(e.status))
    .map((e) => speakers.find((s) => s.id === e.speakerId))
    .filter(Boolean);

  const openMailClient = () => {
    const recipients = emailType === 'Call for speakers' ? [] : engagedSpeakers;
    const mailto = buildEventMailtoLink(emailType, form, recipients, sender);
    window.location.href = mailto;
    setEmailModal(false);
  };

  const socialPost = generateSocialPost(platform, form, engagements, speakers);

  const copySocialPost = async () => {
    await navigator.clipboard.writeText(socialPost.text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      {isEdit && (
        <div className="page-toolbar" style={{ justifyContent: 'flex-end' }}>
          <div className="inline">
            <button className="btn btn-secondary" onClick={() => setEmailModal(true)}>Send email</button>
            <button className="btn btn-secondary" onClick={() => setSocialModal(true)}>Create social post</button>
            <button className="btn btn-primary" onClick={() => setAddSpeakerModal(true)}>+ Add speaker</button>
          </div>
        </div>
      )}
      <div className="page-body stack">
        <ErrorAlert error={errors._} />
        <form onSubmit={handleSubmit} noValidate>
          <div className="stack">
            <div className="grid-3">
              <FormField label="Event name" required id="name" error={errors.name}>
                <Input id="name" value={form.name} onChange={set('name')} required />
              </FormField>
              <FormField label="Date" required id="date" error={errors.date}>
                <Input id="date" type="date" value={form.date} onChange={set('date')} required />
              </FormField>
              <FormField label="Status" id="status">
                <Select id="status" value={form.status} onChange={set('status')}>
                  {EVENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
              </FormField>
            </div>
            <div className="grid-3">
              <FormField label="Start time" id="startTime">
                <Input id="startTime" type="time" value={form.startTime} onChange={set('startTime')} />
              </FormField>
              <FormField label="End time" id="endTime">
                <Input id="endTime" type="time" value={form.endTime} onChange={set('endTime')} />
              </FormField>
              <FormField label="Time zone" id="timeZone">
                <Select id="timeZone" value={form.timeZone} onChange={set('timeZone')}>
                  {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                </Select>
              </FormField>
            </div>
            <div className="grid-3">
              <FormField label="Format" id="format">
                <Select id="format" value={form.format} onChange={set('format')}>
                  {EVENT_FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
                </Select>
              </FormField>
              <FormField label="PDU information" id="pduInfo" hint="e.g. 1.5 PDUs — Ways of Working">
                <Input id="pduInfo" value={form.pduInfo} onChange={set('pduInfo')} />
              </FormField>
              <FormField label="PDU code" id="pduCode">
                <Input id="pduCode" value={form.pduCode} onChange={set('pduCode')} />
              </FormField>
            </div>
            <div className="grid-2">
              <FormField label="Location or virtual meeting link" id="location">
                <Input id="location" value={form.location} onChange={set('location')} />
              </FormField>
              <FormField label="Notes" id="notes">
                <Textarea id="notes" value={form.notes} onChange={set('notes')} rows={1} />
              </FormField>
            </div>
            <div className="inline" style={{ justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create event'}
              </button>
            </div>
          </div>
        </form>

        {isEdit && (
          <section>
            <h2 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-3)' }}>Speakers</h2>
            {engagements.length === 0 ? (
              <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
                No speakers connected to this event yet. Use "+ Add speaker" above.
              </p>
            ) : (
              <div className="data-table" style={{ overflowX: 'auto' }}>
                <table>
                  <thead><tr><th>Speaker</th><th>Talk</th><th>Overview</th><th>Status</th></tr></thead>
                  <tbody>
                    {engagements.map((e) => {
                      const sp = speakers.find((s) => s.id === e.speakerId);
                      return (
                        <tr key={e.id}>
                          <td>{sp ? <Link to={`/speakers/${sp.id}`}>{sp.firstName} {sp.lastName}</Link> : '—'}</td>
                          <td><Link to={`/engagements/${e.id}`}>{e.talkTitle ?? '(untitled)'}</Link></td>
                          <td style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', maxWidth: 320 }}>
                            {e.talkAbstract
                              ? (e.talkAbstract.length > 140 ? `${e.talkAbstract.slice(0, 140)}…` : e.talkAbstract)
                              : '—'}
                          </td>
                          <td><StatusBadge status={e.status} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </div>

      <Modal
        open={emailModal}
        onClose={() => setEmailModal(false)}
        title="Send email"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setEmailModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={openMailClient}>Open in mail client</button>
          </>
        }
      >
        <div className="stack">
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            {emailType === 'Call for speakers'
              ? "This template has no recipient — you'll address it to your own shortlist in your mail client."
              : engagedSpeakers.length > 0
                ? `Addressed to: ${engagedSpeakers.map((s) => `${s.firstName} ${s.lastName}`).join(', ')}`
                : 'No currently-engaged speakers for this event — the draft will open with no recipient.'}
          </p>
          <FormField label="Template" id="eventCommType">
            <Select id="eventCommType" value={emailType} onChange={(e) => setEmailType(e.target.value)}>
              {EVENT_COMM_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </Select>
          </FormField>
        </div>
      </Modal>

      <Modal
        open={addSpeakerModal}
        onClose={() => setAddSpeakerModal(false)}
        title="Add speaker to event"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setAddSpeakerModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={addSpeaker} disabled={!addSpeakerId || addingSpeaker}>
              {addingSpeaker ? 'Adding…' : 'Add'}
            </button>
          </>
        }
      >
        <div className="stack">
          <FormField label="Speaker" required id="addSpeakerId">
            <Select id="addSpeakerId" value={addSpeakerId} onChange={(e) => setAddSpeakerId(e.target.value)}>
              <option value="">Select speaker…</option>
              {speakers.map((s) => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
            </Select>
          </FormField>
          <FormField label="Talk title" id="addTalkTitle" hint="Optional — can be added later">
            <Input id="addTalkTitle" value={addTalkTitle} onChange={(e) => setAddTalkTitle(e.target.value)} />
          </FormField>
          <FormField label="Talk abstract" id="addTalkAbstract" hint="Optional — can be added later">
            <Textarea id="addTalkAbstract" value={addTalkAbstract} onChange={(e) => setAddTalkAbstract(e.target.value)} rows={3} />
          </FormField>
        </div>
      </Modal>

      <Modal
        open={socialModal}
        onClose={() => setSocialModal(false)}
        title="Create social post"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setSocialModal(false)}>Close</button>
            <button className="btn btn-primary" onClick={copySocialPost}>
              {copied ? '✓ Copied' : 'Copy text'}
            </button>
          </>
        }
      >
        <div className="stack">
          <div className="inline" role="tablist" aria-label="Platform">
            <button
              className={`btn btn-sm ${platform === 'linkedin' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setPlatform('linkedin')}
              aria-pressed={platform === 'linkedin'}
            >
              LinkedIn
            </button>
            <button
              className={`btn btn-sm ${platform === 'x' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setPlatform('x')}
              aria-pressed={platform === 'x'}
            >
              X
            </button>
          </div>
          <Textarea
            readOnly
            value={socialPost.text}
            rows={platform === 'x' ? 6 : 12}
            style={{ fontFamily: 'var(--font-sans)' }}
            onClick={(e) => e.target.select()}
            aria-label="Post text"
          />
          {platform === 'x' && (
            <p style={{ fontSize: 'var(--text-xs)', color: socialPost.overLimit ? 'var(--color-danger)' : 'var(--color-text-muted)' }}>
              {socialPost.charCount} / {X_CHAR_LIMIT} characters
              {socialPost.overLimit && ' — over the limit, trim before posting'}
            </p>
          )}
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            Generated from this event's own info{engagements.length > 0 ? ' and its currently-confirmed speaker(s)' : ''}. Copy and paste into LinkedIn or X — posting isn't automated.
          </p>
        </div>
      </Modal>
    </div>
  );
}
